import type { ReactNode } from 'react';
import {
  Clock,
  Coins,
  ExternalLink,
  MapPin,
  Paperclip,
} from '@/components/icons';
import {
  deadlineLabel,
  describeSupportRow,
  grantPhase,
  groupEligibility,
  isFormName,
  noticeEmptyNote,
  noticeStatusNote,
  pickNoticeFile,
  isImageExt,
  regionLabel,
  GRANT_SUPPORT_FILTERS,
  businessAgeLabel,
  CERTIFICATION_LABEL,
  INDUSTRY_LABEL,
  SUPPORT_DETAIL_LABEL,
  SUPPORT_VIEW_LABEL,
  supportHeadline,
  type GrantDetailDto,
  type GrantPhaseKey,
  type GrantSupportDetail,
  type GrantSupportFilter,
  type GrantSupportView,
} from '@zoxo/shared';

import { cn } from '../../lib/utils';
import { api } from '../../lib/api';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

/**
 * 공고 부품 — 상세 화면(routes/news.$id)의 머리 · 우측 카드 · 지원 내용 · 신청 자격 · 공고문, 목록 카드의
 * 알약 · 첨부 배지. 서버와 브라우저가 같은 컴포넌트로 그린다(docs/SSR.md).
 */

/** SPA(grant-card.tsx) 의 PHASE_CLASS 와 같은 값이어야 한다. */
const PHASE_CLASS: Record<GrantPhaseKey, string> = {
  open: 'bg-[#18BB69] text-white',
  closing: 'bg-[#FACC15] font-bold text-[#111827]',
  closed: 'bg-gray-100 text-gray-500',
  result: 'bg-sky-50 text-sky-700',
  ended: 'bg-gray-100 text-gray-400',
};

export function pastTitleClass(phase: GrantPhaseKey): string {
  return phase === 'closed' || phase === 'ended'
    ? 'text-gray-400 line-through decoration-gray-300 decoration-1'
    : 'text-gray-900';
}

/**
 * **기둥 알약은 한 벌이다.** 상세 제목 위의 배지가 원형이고, 홈 네 칸 · 상세 지원 내용 · 콘솔이
 * 같은 꼴(`SUPPORT_PILL` + `SUPPORT_BADGE`)을 쓴다. 두 벌이 섞여 있었다 — 홈과 지원 내용은
 * 11px 반원각, 제목 위는 12px 굵은 글자였다 (2026-09-24 형이 통일 지시).
 */
export const SUPPORT_PILL = 'rounded-md px-2 py-0.5 text-xs font-bold';

export const SUPPORT_BADGE: Record<GrantSupportFilter, string> = {
  // 자금·바우처는 돈을 주는 기둥이라 가장 중요하다 — 가장 진한 빨강.
  grant: 'bg-[#DF301C] text-white',
  loan: 'bg-[#4E1F6E] text-white',
  facility: 'bg-[#FF9100] text-white',
  program: 'bg-[#1B2CC1] text-white',
};

const DETAIL_TYPES = GRANT_SUPPORT_FILTERS;

/**
 * 상세의 머리 — **배지 → 기관 → 제목 → 기본 정보 표.**
 *
 * 목록에서 보이던 것(진행 단계·유형)이 상세에 들어오면 사라지던 것을 고친다.
 * 제목 위에 단계 배지와 유형 배지를 세우고, 제목 바로 아래에 모집 기간·유형·
 * 지역·출처·첨부를 한 표로 모은다 — 독자가 스크롤 없이 「언제까지·어떤·어디·
 * 어디서」를 한 번에 읽는다.
 *
 */
export function GrantDetailHead({ grant, now }: { grant: GrantDetailDto; now: Date }) {
  const phase = grantPhase(grant, now);
  // 대표 유형이 먼저, 나머지는 정해진 순서. 해석 결과가 있는 유형만 세운다.
  const types = [
    ...(grant.primaryType ? [grant.primaryType] : []),
    ...DETAIL_TYPES.filter((k) => k !== grant.primaryType),
  ].filter((k) => grant.support?.[k]);
  return (
    <header>
      <div className="flex flex-wrap items-center gap-1.5">
        {/* 단계만. 남은 날은 우측 카드가 크게 말한다. */}
        <span className={cn(SUPPORT_PILL, PHASE_CLASS[phase.key])}>
          {phase.label}
        </span>
        {types.map((k) => (
          <span
            key={k}
            className={cn(SUPPORT_PILL, SUPPORT_BADGE[k])}
          >
            {SUPPORT_VIEW_LABEL[k]}
          </span>
        ))}
      </div>
      {/* 제목 위에는 배지만. 기관·지역·분류는 아래 정리표와 우측 카드에 있다 — 여기 또 두면 난립한다. */}
      <h1 className="mt-3 text-xl font-bold leading-snug text-gray-900 sm:text-2xl">
        {grant.title}
      </h1>

      {/* 유형·지역은 위 배지와 우측 카드가 말한다. 마감까지 남은 날도 배지에 있다. */}
      <dl className="mt-5 divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white px-4">
        <DetailRow label="모집 기간">
          <span className="tabular-nums">
            {grant.applyStart ? `${grant.applyStart} ~ ` : ''}
            {grant.applyEnd ?? '상시 (마감일 없음)'}
          </span>
        </DetailRow>
        {/* K-Startup 은 허용 구간을 쉼표 목록으로 준다 — 일곱 구간 전부면 「전체」, 이어지면 범위 (businessAgeLabel). */}
        {grant.businessAge ? <DetailRow label="업력">{businessAgeLabel(grant.businessAge)}</DetailRow> : null}
        {grant.executingInstitution ? (
          <DetailRow label="수행기관">{grant.executingInstitution}</DetailRow>
        ) : null}
        {/* 신청 기업의 업종 (REFACTOR §3.2). 해석기가 채운다 — 재해석 전 공고는 이 줄이 없다. */}
        {grant.allIndustries || grant.industries?.list?.length ? (
          <DetailRow label="업종">
            <span className="flex flex-wrap gap-1">
              {grant.allIndustries ? <span className={CODE_PILL}>전 업종</span> : null}
              {(grant.industries?.list ?? []).map((c) => (
                <span key={c} className={cn(CODE_PILL, c === grant.industries.primary && 'font-semibold')}>
                  {(INDUSTRY_LABEL as Record<string, string>)[c] ?? c}
                </span>
              ))}
            </span>
          </DetailRow>
        ) : null}
        <DetailRow label="출처">{grant.sourceName}</DetailRow>
        {/* 출처가 준 분류 문자열 그대로. 두 출처의 어휘가 섞여 있지만 정리할 만큼 쓰이지 않는다. */}
        {grant.supportField ? <DetailRow label="분류">{grant.supportField}</DetailRow> : null}
        {grant.attachments.length > 0 ? (
          <DetailRow label={`첨부 파일 ${grant.attachments.length}`}>
            <ul className="space-y-1">
              {grant.attachments.map((a) => (
                <li key={a.url}>
                  <a
                    href={a.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-strong underline underline-offset-2"
                  >
                    {/* 기업마당 API 가 파일명의 공백을 `+` 로 준다 (`붙임1.+2026+세종…`). */}
                    {a.name.replace(/\+/g, ' ')}
                  </a>
                  {/*
                    **`kind` 가 아니라 이름으로 판단한다.** 추출 배치가 쓰는 판정과
                    같은 함수다 — 다르면 화면이 거짓말을 한다.
                  */}
                  {isFormName(a.name) ? (
                    <span className="ml-2 text-xs text-gray-500">서식</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </DetailRow>
        ) : null}
      </dl>
    </header>
  );
}

/**
 * 우측에 따라다니는 요약·행동 카드 — **「지금 뭘 할 수 있는가」.**
 *
 * 본문이 길어 스크롤이 깊어져도 마감까지 남은 날과 원문으로 가는 길이 화면에
 * 남아 있어야 한다. 데스크톱(lg)에서는 제목 옆 우상단에 붙어 따라오고, 모바일은
 * 본문 뒤에 놓인다 — 머리 배지에 같은 정보가 있어 위쪽이 비지 않는다.
 */
/** 우측 카드 버튼 한 벌 — 신청 · 원문 · 공고문 · 신고가 같은 꼴을 쓴다. */
export const ASIDE_BUTTON =
  'flex h-11 w-full items-center justify-center gap-2 rounded-lg text-sm font-bold transition-colors';

export const ASIDE_BUTTON_OUTLINE = cn(ASIDE_BUTTON, 'border border-gray-200 bg-white text-gray-800 hover:bg-gray-50');

export function GrantDetailAside({
  grant,
  now,
  action,
  footer,
}: {
  grant: GrantDetailDto;
  now: Date;
  /** 카드 우상단(16px, 16px)에 놓는 것 — SPA 의 북마크 토글. SSR 은 비워 둔다(익명). */
  action?: ReactNode;
  /** 버튼 묶음 맨 아래 — SPA 의 「잘못된 내용 신고」(로그인만). SSR 은 비워 둔다. */
  footer?: ReactNode;
}) {
  const phase = grantPhase(grant, now);
  const deadline = deadlineLabel(grant.applyEnd, now);
  const live = phase.key === 'open' || phase.key === 'closing';
  // 지났으면 검정 「종료」. 3일 이하면 빨강 D-n — 지금 움직여야 한다는 뜻이다.
  // 그 사이는 **이 공고의 대표 유형 색**이다 — 보조금 상세의 기본 색은 초록이 아니라 빨강이다.
  const urgent = live && deadline.days !== null && deadline.days <= 3;
  const typeTone = grant.primaryType ? SUPPORT_TEXT_TONE[grant.primaryType] : 'text-[#00894F]';
  const big = !live
    ? '종료'
    : deadline.days === null
      ? '상시 모집'
      : deadline.days === 0
        ? 'D-Day'
        : `D-${deadline.days}`;
  const bigTone = !live ? 'text-gray-900' : urgent ? 'text-[#DF301C]' : typeTone;
  const money = grant.primaryType ? grant.support?.[grant.primaryType] : undefined;
  const headline = grant.primaryType && money ? supportHeadline(grant.primaryType, money) : null;
  const notice = grant.attachments.find((a) => a.kind === 'notice');
  const region = grant.nationwide ? '전국' : (regionLabel(grant, { max: 3 }) ?? '지역 미기재');
  const button = ASIDE_BUTTON;

  return (
    // 우측 카드는 머리 89 + 기둥 알약 줄 57 = 146px 아래에 붙는다.
    <aside className="lg:sticky lg:top-[162px] lg:self-start">
      <div className="relative rounded-2xl border border-gray-200 bg-white p-5 shadow-xs">
        {action}
        <p className="text-xs font-semibold text-gray-500">{live ? '모집 중' : '모집 상태'}</p>
        <p className={cn('mt-1 text-2xl font-extrabold tabular-nums', bigTone)}>{big}</p>
        <ul className="mt-4 space-y-2 text-sm text-gray-700">
          <li className="flex items-center gap-2">
            <MapPin className="size-3.5 text-gray-400" />
            {region}
          </li>
          <li className="flex items-center gap-2 tabular-nums">
            <Clock className="size-3.5 text-gray-400" />
            {grant.applyEnd ? `${grant.applyEnd} 까지` : '마감일 없음'}
          </li>
          {headline ? (
            <li className="flex items-center gap-2 tabular-nums">
              <Coins className="size-3.5 text-gray-400" />
              {headline}
            </li>
          ) : null}
        </ul>
        <div className="mt-5 space-y-2">
          {/* 신청은 기관에서 한다. 그 길을 분명하게 낸다. */}
          {grant.applyUrl ? (
            <a
              href={grant.applyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(button, 'bg-gray-900 text-white hover:bg-gray-800')}
            >
              신청 페이지로
            </a>
          ) : null}
          {grant.detailUrl ? (
            <a
              href={grant.detailUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                button,
                'border border-gray-200 bg-white text-gray-800 hover:bg-gray-50',
              )}
            >
              <ExternalLink className="size-3.5" />
              원문 공고 보기
            </a>
          ) : null}
          {notice ? (
            <a
              href={notice.url}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                button,
                'border border-gray-200 bg-white text-gray-800 hover:bg-gray-50',
              )}
            >
              공고문 내려받기 ({notice.ext})
            </a>
          ) : null}
          {footer}
        </div>
      </div>
    </aside>
  );
}

/** 자격 · 업종 코드 알약. 상세 정리표와 신청 자격 절이 같은 꼴을 쓴다. */
const CODE_PILL = 'rounded border border-gray-200 bg-gray-50 px-1.5 py-px text-[11px] text-gray-700';

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-4 py-2.5 text-sm">
      <dt className="w-20 shrink-0 text-gray-500">{label}</dt>
      <dd className="min-w-0 flex-1 text-gray-900">{children}</dd>
    </div>
  );
}

/* ══════════════════════════════════════════════════ 해석 결과 (지원 내용·자격) */

/**
 * 첫 화면의 네 칸(§SUPPORT_BADGE)과 같은 규칙이다 — **테두리는 유형을 말하지
 * 않는다.** 유형은 배지가 말한다. 배경의 옅은 색조만 남겨 카드끼리 구분되게
 * 하고, 테두리는 기본 회색(`--color-border`)으로 통일한다.
 */
const SUPPORT_TONE_DETAIL: Record<GrantSupportFilter, string> = {
  grant: 'border-border bg-red-50/60',
  loan: 'border-border bg-purple-50/60',
  facility: 'border-border bg-orange-50/60',
  program: 'border-border bg-sky-50/40',
};

/** 기둥 색을 글자에 쓸 때. 알약 배경색과 같은 계열이되 흰 바탕에서 읽히는 농도. */
const SUPPORT_TEXT_TONE: Record<GrantSupportFilter, string> = {
  grant: 'text-[#DF301C]',
  loan: 'text-[#4E1F6E]',
  facility: 'text-[#FF9100]',
  program: 'text-[#1B2CC1]',
};

/**
 * **이 화면의 존재 이유.**
 *
 * 상세까지 온 사람의 질문은 「이 공고가 나한테 얼마를 주나」다. 그 답을 원문
 * 8,000자에서 찾게 하면 우리는 링크 모음일 뿐이다. 해석기가 뽑고 검증 게이트가
 * 원문에 대고 대조한 값을 **항목마다** 편다.
 *
 * 숫자 옆에 근거 문장을 붙인다. 우리가 지어낸 숫자가 아니라는 것을 독자가
 * 스스로 확인할 수 있어야 하고, 우리가 틀렸을 때 어디서 틀렸는지 보여야 한다.
 *
 * **아직 해석 안 한 공고는 이 절을 통째로 안 그린다.** 「분석 중」 같은 빈 말은
 * 자리만 차지하고 아무것도 안 알려준다.
 */
export function GrantSupportRows({
  detail,
  support,
}: {
  detail: GrantSupportDetail | undefined;
  /** 카드와 같은 요약. 행을 읽기 전에 「얼마나」를 한 줄로 먼저 본다. */
  support?: GrantSupportView;
}) {
  const rows = detail?.rows ?? [];
  const headlines = (Object.keys(SUPPORT_VIEW_LABEL) as GrantSupportFilter[])
    .map((k) => ({ k, text: support?.[k] ? supportHeadline(k, support[k]!) : null }))
    .filter((h): h is { k: GrantSupportFilter; text: string } => Boolean(h.text));
  if (rows.length === 0) return null;

  return (
    <section className="mt-6">
      {/*
            **해석 상태는 여기서 한 번만 말한다.**

            항목마다 「확인하지 못했다」 같은 말을 두면 독자가 그걸 진행 상태로
            읽는다 — 실제로 그 질문을 받았다. 완료 시각을 절 제목 옆에 한 번
            두면 아래 항목들이 전부 **완료된 결과**로 읽힌다.

            해석 안 한 공고는 이 절 자체가 안 그려지므로(위 early return),
            이 자리에 「대기 중」은 올 수 없다.
          */}
      <div className="flex flex-wrap items-baseline gap-x-2">
        <h2 className="text-sm font-semibold text-gray-900">지원 내용</h2>
        <span className="text-[11px] text-gray-400">공고문에서 뽑아 원문과 대조한 결과입니다</span>
      </div>
      {/*
            **요약 한 줄이 행보다 먼저다.** 조건별 구간은 행이 다섯 개라 하나씩
            읽으면 「최대 1,500만원」「최대 8,000만원」…이 따로 보인다. 카드가
            말하는 「조건별 1,500만원~15억원」을 여기서도 먼저 말한다.
          */}
      {headlines.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
          {headlines.map((h) => (
            <span key={h.k}>
              <span className="mr-1 text-[11px] font-medium text-gray-400">
                {SUPPORT_VIEW_LABEL[h.k]}
              </span>
              <span className="font-semibold tabular-nums text-gray-900">{h.text}</span>
            </span>
          ))}
        </div>
      ) : null}
      <div className="mt-3 space-y-2">
        {rows.map((row, i) => {
          const facts = describeSupportRow(row);
          return (
            <div
              key={`${row.table}-${row.name ?? ''}-${row.condition ?? i}`}
              className={cn('rounded-lg border p-4', row.values.ancillary === true ? SUPPORT_TONE_DETAIL.program : SUPPORT_TONE_DETAIL[row.table])}
            >
              <div className="flex flex-wrap items-baseline gap-2">
                {/* 부대비용(0052)은 프로그램 참가에 딸린 돈 — 프로그램 색으로 「부대비용」이라 부른다. */}
                <span
                  className={cn(
                    SUPPORT_PILL,
                    row.values.ancillary === true ? SUPPORT_BADGE.program : SUPPORT_BADGE[row.table],
                  )}
                >
                  {row.values.ancillary === true ? '부대비용' : SUPPORT_DETAIL_LABEL[row.table]}
                </span>
                {/*
                      이름이 없으면 유형만 쓴다. 원문 대조에 실패한 이름은
                      NULL 로 남는데, 그 자리에 「(이름 없음)」을 넣으면
                      독자에게 우리 사정을 떠넘기는 것이다.
                    */}
                {row.name ? (
                  <h3 className="text-[15px] font-semibold text-gray-900">
                    {row.name}
                    {/* 조건별 차등이면 그 조건을 이름 옆에 (`0030`). */}
                    {row.condition ? (
                      <span className="ml-1.5 font-medium text-gray-500">· {row.condition}</span>
                    ) : null}
                  </h3>
                ) : row.condition ? (
                  <h3 className="text-[15px] font-semibold text-gray-900">{row.condition}</h3>
                ) : null}
              </div>

              {facts.length > 0 ? (
                <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
                  {facts.map((f) => (
                    <div key={f.label} className="min-w-0">
                      <dt className="text-[11px] text-gray-500">
                        {f.label}
                        {/*
                              검증 안 거친 값은 그렇게 말한다. 대조한 숫자와
                              같은 무게로 두면 전부의 신뢰가 같이 떨어진다.
                            */}
                        {f.unverified ? <span className="ml-1 text-gray-400">(미검증)</span> : null}
                      </dt>
                      <dd className="text-sm font-semibold tabular-nums text-gray-900">{f.text}</dd>
                      {f.quote ? (
                        <dd className="mt-0.5 truncate text-[11px] text-gray-400" title={f.quote}>
                          「{f.quote}」
                        </dd>
                      ) : null}
                    </div>
                  ))}
                </dl>
              ) : (
                /*
                      숫자가 하나도 안 나온 항목. 이름이 곧 설명이다.

                      **「확인하지 못했다」고 쓰지 않는다.** 진행 중으로 읽힌다 —
                      실제로 이 문구를 보고 「아직 해석이 안 끝난 거냐」는 질문을
                      받았다. 해석은 끝났고 이 항목에 숫자가 없다는 **완료된
                      사실**인데, 문장이 과정을 말하고 있었다.

                      **「공고문에 없다」고도 쓰지 않는다.** 값이 빈 이유는
                      모델이 못 찾았거나, 찾았지만 원문 대조에 실패했거나, 정말
                      없거나 셋인데 화면은 구별할 수 없다. 실제로 더인벤션랩
                      공고에서 원문에 「일반형 최대 11억」이 멀쩡히 있는데도 이
                      자리가 비었다 — 없다고 썼으면 거짓말이었다.

                      그래서 **아무 주장도 안 하는 말**을 쓴다. 상태 표시는
                      항목이 아니라 절 제목이 맡는다 (§AnalysisNote).
                    */
                <p className="mt-2 text-xs text-gray-400">금액·기간 정보 없음</p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** 신청 자격 — 정리표 바로 아래, 같은 모양(흰 배경·테두리)의 표. */
export function GrantEligibility({ detail }: { detail: GrantSupportDetail | undefined }) {
  const groups = groupEligibility(detail?.eligibility ?? []);
  if (groups.length === 0) return null;

  return (
    <section className="mt-6">
      <h2 className="text-sm font-semibold text-gray-900">신청 자격</h2>
      <dl className="mt-3 divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white px-4">
        {groups.map((g) => (
          <div key={g.kind} className="flex gap-4 py-2.5">
            <dt
              className={cn(
                'w-20 shrink-0 text-sm',
                /*
                      제외 대상은 다른 색이다. 「받을 수 있는 조건」과 정반대
                      방향인데 같은 회색으로 두면 자격 요건으로 읽힌다.
                    */
                g.kind === 'exclusion' ? 'font-medium text-rose-700' : 'text-gray-500',
              )}
            >
              {g.label}
            </dt>
            <dd className="min-w-0 flex-1">
              <ul className="space-y-1">
                {g.items.map((it, i) => (
                  <li key={`${it.valueText}-${i}`} className="text-sm text-gray-900">
                    {it.valueText}
                    {/* 자격 · 업종 코드 (REFACTOR §3.2). 표에 든 값만 온다 — 코드 라벨을 알약으로. */}
                    {it.code ? (
                      <span className={cn(CODE_PILL, 'ml-1.5 align-middle')}>
                        {(CERTIFICATION_LABEL as Record<string, string>)[it.code] ??
                          (INDUSTRY_LABEL as Record<string, string>)[it.code] ??
                          it.code}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/**
 * 배치가 읽은 원문 — **원문 | 텍스트 두 탭.**
 *
 * 「공고문 원문」은 출처 파일을 API 가 PDF 로 다시 주는 것을 iframe 으로 띄운다
 * (`/grants/:id/notice.pdf`; hwp 계열은 서버에서 rhwp 로 변환). 「공고문
 * 텍스트」는 배치가 뽑은 글자다 — 우리가 무엇을 보고 그렇게 판정했는지 확인하는
 * 자리라 지우지 않는다.
 *
 * 탭은 라디오 + CSS 다. SSR 과 SPA 가 같은 마크업을 내고 hydrate 전에도 눌린다.
 * 보여줄 파일이 없으면(zip·이미지·첨부 없음) 텍스트 탭만 그린다.
 */
/**
 * 원문 PDF 주소. `v` 는 CDN 캐시 키를 바꾸는 손잡이다 — 출처의 빈 파일을 200 으로 캐시했던
 * 하루짜리 항목(2026-09-11)을 우회하려고 1→2 로 올렸다. 응답 형식이 바뀌면 또 올린다.
 */
const noticePdfUrl = (id: string) => `/api/v1/grants/${id}/notice.pdf?v=2`;

export function GrantNoticeText({ grant }: { grant: GrantDetailDto }) {
  const picked = pickNoticeFile(grant.attachments);
  /*
    출처가 빈 파일을 주는 공고가 있다(기업마당 fileSn=0). API 는 404 를 주지만 iframe 은
    HTTP 오류를 알리지 않아 브라우저 뷰어의 「로드하지 못했습니다」 상자만 남는다. 그래서
    hydrate 뒤에 HEAD 로 한 번 찔러 보고, 없으면 원문 탭을 접고 텍스트만 보여준다.
    SSR 은 원문 탭을 그린 채로 나가고(있는 경우가 대부분이다), 클라이언트가 바로잡는다.
  */
  const [pdfMissing, setPdfMissing] = useState(false);
  /*
    「공고문 텍스트」를 **누른 적이 있나.** 누르기 전에는 글자를 받지 않는다 —
    받아 버리면 렌더된 DOM 에 출처 기관의 글자가 그대로 들어가고, 그것이 이 화면을
    고치는 이유 자체다(GrantNoticeText 주석).
  */
  const [textOpened, setTextOpened] = useState(false);
  useEffect(() => {
    if (!picked) return;
    let cancelled = false;
    fetch(noticePdfUrl(grant.id), { method: 'HEAD' })
      .then((r) => {
        // 404 만 「없다」다. 5xx·타임아웃은 변환이 느리거나 잠깐 실패한 것이라 탭을 접지 않는다.
        if (!cancelled && r.status === 404) setPdfMissing(true);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [grant.id, picked]);
  /*
    탭은 hydrate 전에도 눌린다(라디오 + CSS). 그때 누른 사람은 change 이벤트를
    React 가 못 듣는다 — 마운트 때 한 번 라디오 상태를 직접 읽어 이어 준다.
    크롤러는 누르지 않으므로 여기서 켜지지 않는다.
  */
  useEffect(() => {
    const el = document.getElementById('notice-tab-text');
    if (el instanceof HTMLInputElement && el.checked) setTextOpened(true);
  }, []);

  const file = pdfMissing ? null : picked;
  const tab =
    'cursor-pointer border-b-2 border-transparent px-4 py-2.5 text-sm font-semibold text-gray-500 hover:text-gray-900';
  return (
    <section className="mt-6">
      {file ? (
        <>
          <input
            type="radio"
            name="notice-tab"
            id="notice-tab-pdf"
            className="peer/pdf sr-only"
            defaultChecked
          />
          <input
            type="radio"
            name="notice-tab"
            id="notice-tab-text"
            className="peer/text sr-only"
            onChange={() => setTextOpened(true)}
          />
          {/* 눌린 탭의 밑줄. Tailwind 가 소스에서 글자 그대로 찾아야 하므로 문자열을 조립하지 않는다. */}
          <div className="flex border-b border-gray-200 peer-checked/pdf:[&>label:first-child]:border-gray-900 peer-checked/pdf:[&>label:first-child]:text-gray-900 peer-checked/text:[&>label:last-child]:border-gray-900 peer-checked/text:[&>label:last-child]:text-gray-900">
            <label htmlFor="notice-tab-pdf" className={tab}>
              공고문 원문
            </label>
            <label htmlFor="notice-tab-text" className={tab}>
              공고문 텍스트
            </label>
          </div>
          <div className="hidden peer-checked/pdf:block">
            {isImageExt(file.ext) ? (
              /* 원문이 포스터 한 장(K-Startup jpg 등). 뷰어 없이 그림을 그대로 — 출처는 nosniff 라 API 가 대신 준다. */
              <img
                src={noticePdfUrl(grant.id)}
                alt="공고문 원문"
                loading="lazy"
                className="mt-3 w-full rounded-lg border border-gray-200 bg-gray-50"
              />
            ) : (
              <iframe
                /*
                  PDF 열기 파라미터(브라우저 뷰어가 읽는다). 썸네일 사이드바를 접고
                  쪽을 프레임 폭에 맞춘다 — 사이드바가 열리면 글자가 작아진다.
                */
                src={`${noticePdfUrl(grant.id)}#navpanes=0&view=FitH`}
                title="공고문 원문"
                loading="lazy"
                className="mt-3 h-[36rem] w-full rounded-lg border border-gray-200 bg-gray-50 sm:h-[70rem]"
              />
            )}
            <p className="mt-1.5 text-xs text-gray-400">
              {file.ext === 'pdf'
                ? '출처 파일 그대로'
                : isImageExt(file.ext)
                  ? '이미지 파일 그대로'
                  : `${file.ext} 파일을 PDF 로 변환해 보여드립니다`}{' '}
              · {file.name.replace(/\+/g, ' ')}
            </p>
          </div>
          <div className="hidden peer-checked/text:block">
            <NoticeTextBody grant={grant} open={textOpened} />
          </div>
        </>
      ) : (
        <>
          <h2 className="text-sm font-semibold text-gray-900">공고문 텍스트</h2>
          {pdfMissing ? (
            <p className="mt-1 text-xs text-gray-500">출처의 원문 파일이 비어 있어 텍스트만 보여드립니다.</p>
          ) : null}
          {textOpened ? (
            <NoticeTextBody grant={grant} open />
          ) : (
            /* 탭이 없는 공고(첨부 없음·빈 파일)에도 누를 자리는 있어야 한다. */
            <button
              type="button"
              onClick={() => setTextOpened(true)}
              className="mt-3 w-full cursor-pointer rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-100"
            >
              공고문 텍스트 보기
            </button>
          )}
        </>
      )}
    </section>
  );
}

/**
 * 공고문 글자 — **상세 응답에 없다.** 탭을 연 뒤에 따로 받는다.
 *
 * 상세 DTO 에 실으면 SSR 문서에 두 번 박힌다(보이는 `<pre>` 에 한 번, hydration
 * 페이로드에 한 번). 표본 8장의 중앙값 72% 가 출처 기관 PDF 의 축자 복제였고,
 * 애드센스가 사이트를 「복제된 콘텐츠」로 판정한 근거가 그 상태였다.
 * `staleTime: Infinity` — 배치가 다시 쓰기 전엔 바뀌지 않는 글자다.
 */
function useNoticeText(id: string, open: boolean) {
  return useQuery({
    queryKey: ['grant-notice-text', id],
    queryFn: () => api.get<{ text: string | null }>(`/grants/${id}/notice-text`),
    enabled: open,
    staleTime: Infinity,
  });
}

function NoticeTextBody({ grant, open }: { grant: GrantDetailDto; open: boolean }) {
  const { data, isPending, isError } = useNoticeText(grant.id, open);
  return (
    <div className="mt-3 rounded-lg border border-gray-200">
      <p className="px-4 py-2.5 text-xs tabular-nums text-gray-500">{noticeStatusNote(grant)}</p>
      {isPending ? (
        <p className="border-t border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">불러오는 중…</p>
      ) : isError ? (
        <p className="border-t border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
          공고문 텍스트를 불러오지 못했다.
        </p>
      ) : data?.text ? (
        <pre className="max-h-[70rem] overflow-y-auto whitespace-pre-wrap break-words border-t border-gray-200 bg-gray-50 p-4 font-mono text-xs leading-relaxed text-gray-800">
          {data.text}
        </pre>
      ) : (
        <p className="border-t border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
          {noticeEmptyNote(grant)}
        </p>
      )}
    </div>
  );
}

/**
 * 첨부 개수 — 출처 알약 오른쪽에 클립 아이콘 + 숫자. 출처 알약과 같은 모양·색.
 *
 * 예전엔 금액 아래 색깔 알약(「첨부 3」)이었다. 첨부는 제목의 속성이지 금액의 속성이
 * 아니라 메타 줄로 옮겼고, 색을 빼 출처와 같은 회색으로 둔다 (2026-09-12).
 */
export function AttachmentPill({ count }: { count: number }) {
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 rounded border border-gray-200 px-1.5 py-px text-[11px] tabular-nums text-gray-400"
      title={count === 0 ? '첨부 없음' : `첨부 ${count}개`}
    >
      <Paperclip className="size-3" />
      {count}
    </span>
  );
}

